'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Typography, Tooltip } from 'src/components';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faEllipsisVertical,
  faPencil,
  faCopy,
  faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { createPortal } from 'react-dom';

interface RoleActionsDropdownProps {
  onEdit: () => void;
  onCopy: () => void;
  onDelete: () => void;
  canEdit: boolean;
  canDelete: boolean;
  disabled?: boolean;
}

// * Roles with assigned users cannot be deleted
// * System roles cannot be edited or deleted
const RoleActionsDropdown: React.FC<RoleActionsDropdownProps> = ({
  onEdit,
  onCopy,
  onDelete,
  canEdit = true,
  canDelete = true,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleItemClick = (handler: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    handler();
    setIsOpen(false);
  };

  const handleItemKeyDown =
    (handler: () => void) => (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handler();
        setIsOpen(false);
      }
    };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setIsOpen(!isOpen);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const renderDeleteButton = () => {
    if (canDelete) {
      return (
        <div
          role="menuitem"
          tabIndex={0}
          onClick={handleItemClick(onDelete)}
          onKeyDown={handleItemKeyDown(onDelete)}
          className="flex items-center gap-2 px-4 py-2 hover:bg-neutral-50 cursor-pointer text-sm text-red-600 hover:text-red-700"
          data-testid="role-actions-dropdown-delete-button"
        >
          <Icon icon={faTrash} className="w-4 h-4" />
          <Typography className="text-red-600 hover:text-red-700 text-sm">
            Delete
          </Typography>
        </div>
      );
    }

    return (
      <Tooltip
        content="This role cannot be deleted because it has assigned users"
        position="top"
      >
        <div
          role="menuitem"
          className="flex items-center gap-2 px-4 py-2 cursor-not-allowed text-sm text-neutral-300"
          data-testid="role-actions-dropdown-delete-button-disabled"
        >
          <Icon icon={faTrash} className="w-4 h-4" />
          <Typography className="text-neutral-300 text-sm">Delete</Typography>
        </div>
      </Tooltip>
    );
  };
  if (disabled) {
    return (
      <Tooltip content="Only available to support users" position="top">
        <div className="text-neutral-400 p-2 cursor-not-allowed">
          <Icon icon={faEllipsisVertical} className="w-4 h-4" />
        </div>
      </Tooltip>
    );
  }
  return (
    <div ref={triggerRef} className="relative">
      <div
        role="button"
        tabIndex={0}
        onClick={disabled ? undefined : handleClick}
        onKeyDown={handleKeyDown}
        className="text-neutral-400 p-2 cursor-pointer hover:text-neutral-700"
        data-testid="role-actions-dropdown-trigger"
      >
        <Icon icon={faEllipsisVertical} className="w-4 h-4" />
      </div>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed z-50 bg-white rounded-lg shadow-lg border border-neutral-200 w-36"
            style={{
              top: triggerRef.current?.getBoundingClientRect().bottom ?? 0,
              right:
                window.innerWidth -
                (triggerRef.current?.getBoundingClientRect().right ?? 0),
            }}
          >
            <div className="py-2">
              {canEdit && (
                <div
                  role="menuitem"
                  tabIndex={0}
                  onClick={handleItemClick(onEdit)}
                  onKeyDown={handleItemKeyDown(onEdit)}
                  className="flex items-center gap-2 px-4 py-2 hover:bg-neutral-50 cursor-pointer text-sm"
                  data-testid="role-actions-dropdown-edit-button"
                >
                  <Icon icon={faPencil} className="w-4 h-4" />
                  <Typography className="text-neutral-600 text-sm">
                    Edit
                  </Typography>
                </div>
              )}
              <div
                role="menuitem"
                tabIndex={0}
                onClick={handleItemClick(onCopy)}
                onKeyDown={handleItemKeyDown(onCopy)}
                className="flex items-center gap-2 px-4 py-2 hover:bg-neutral-50 cursor-pointer text-sm"
                data-testid="role-actions-dropdown-copy-button"
              >
                <Icon icon={faCopy} className="w-4 h-4" />
                <Typography className="text-neutral-600 text-sm">
                  Copy
                </Typography>
              </div>
              {canEdit && (
                <>
                  <div className="px-4 py-2">
                    <div className="h-px bg-neutral-200" />
                  </div>
                  {renderDeleteButton()}
                </>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default RoleActionsDropdown;
